import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, ChevronUp, ChevronDown, Trash2, Edit } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/atoms/Button';
import { IconButton } from '@/components/atoms/IconButton';
import { Badge } from '@/components/atoms/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/molecules/Card';
import { useActivities } from '@/hooks/useBackofficeData';
import { buildRoute, ROUTES } from '@/constants/routes';
import type { Activity } from '@/types/story.types';
import type { ActivityEditorPageProps } from './ActivityEditorPage.types';
import styles from './ActivityEditorPage.module.scss';

export function ActivityEditorPage({ className, ...props }: ActivityEditorPageProps) {
  const navigate = useNavigate();
  const { storyId: storyId } = useParams<{ storyId: string }>();
  const { activities, isLoading, deleteActivity, reorderActivity } = useActivities(storyId);

  const handleBack = () => {
    navigate(-1);
  };

  const handleAddActivity = () => {
    navigate(buildRoute(ROUTES.BACKOFFICE.ACTIVITY_CREATE, { storyId: storyId ?? '' }));
  };

  const handleEditActivity = (activity: Activity) => {
    navigate(
      buildRoute(ROUTES.BACKOFFICE.ACTIVITY_EDIT, { storyId: storyId ?? '', activityId: activity.id })
    );
  };

  const handleDeleteActivity = async (activityId: string) => {
    if (window.confirm('¿Seguro que quieres eliminar esta actividad?')) {
      try {
        await deleteActivity(activityId);
      } catch (error) {
        console.error('Error deleting activity:', error);
        alert('Error al eliminar la actividad');
      }
    }
  };

  const handleMoveUp = async (activity: Activity, index: number) => {
    if (index === 0) return;
    try {
      await reorderActivity(activity.id, index - 1);
    } catch (error) {
      console.error('Error reordering activity:', error);
      alert('Error al reordenar la actividad');
    }
  };

  const handleMoveDown = async (activity: Activity, index: number) => {
    if (index === activities.length - 1) return;
    try {
      await reorderActivity(activity.id, index + 1);
    } catch (error) {
      console.error('Error reordering activity:', error);
      alert('Error al reordenar la actividad');
    }
  };

  const getActivityTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      multiple_choice: 'Opción múltiple',
      fill_in_blank: 'Completar huecos',
      fill_in_blank_tap: 'Completar huecos (tap)',
      true_false: 'Verdadero/Falso',
      matching: 'Emparejar',
      ordering: 'Ordenar',
      open_ended: 'Respuesta abierta',
    };
    return labels[type] || type;
  };

  if (isLoading) {
    return (
      <div className={cn(styles.activityEditor, className)} {...props}>
        <div className={styles.activityEditor__loading}>Cargando actividades...</div>
      </div>
    );
  }

  return (
    <div className={cn(styles.activityEditor, className)} {...props}>
      <div className={styles.activityEditor__header}>
        <IconButton icon={ArrowLeft} onClick={handleBack} aria-label="Volver a la historia" />
        <Button leftIcon={<Plus />} onClick={handleAddActivity}>
          Añadir actividad
        </Button>
      </div>

      <h1 className={styles.activityEditor__title}>Editor de actividades</h1>
      <p className={styles.activityEditor__subtitle}>
        ID historia: {storyId} • {activities.length} {activities.length === 1 ? 'actividad' : 'actividades'}
      </p>

      <div className={styles.activityEditor__content}>
        {activities.length === 0 ? (
          <Card>
            <CardContent className={styles.activityEditor__empty}>
              <p>Sin actividades. Haz clic en "Añadir actividad" para crear una.</p>
            </CardContent>
          </Card>
        ) : (
          <div className={styles.activityEditor__list}>
            {activities.map((activity, index) => (
              <Card key={activity.id} className={styles.activityEditor__item}>
                <CardHeader className={styles.activityEditor__itemHeader}>
                  <div className={styles.activityEditor__itemInfo}>
                    <div className={styles.activityEditor__itemBadges}>
                      <Badge variant="default">{getActivityTypeLabel(activity.type)}</Badge>
                      <Badge variant="info">{activity.points} pts</Badge>
                      <span className={styles.activityEditor__itemOrder}>Orden: {activity.order}</span>
                    </div>
                    <CardTitle>{activity.question}</CardTitle>
                    {activity.explanation && (
                      <p className={styles.activityEditor__itemExplanation}>{activity.explanation}</p>
                    )}
                  </div>
                  <div className={styles.activityEditor__itemActions}>
                    <div className={styles.activityEditor__itemReorder}>
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<ChevronUp />}
                        onClick={() => handleMoveUp(activity, index)}
                        disabled={index === 0}
                        aria-label="Subir"
                      >
                        <span className="sr-only">Subir</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<ChevronDown />}
                        onClick={() => handleMoveDown(activity, index)}
                        disabled={index === activities.length - 1}
                        aria-label="Bajar"
                      >
                        <span className="sr-only">Bajar</span>
                      </Button>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Edit />}
                      onClick={() => handleEditActivity(activity)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      leftIcon={<Trash2 />}
                      onClick={() => handleDeleteActivity(activity.id)}
                    >
                      Eliminar
                    </Button>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
